import { useEffect, useState, type ReactNode } from 'react';
import { Section, SectionHeading } from '~/components/layout';
import { useContent } from '~/lib/useLanguage';
import type { DisplayCurrency } from '~/content/ui';
import { cn } from '~/lib/cn';
import { currencyFlagSrc } from '~/lib/currency-flag';
import {
  DEFAULT_COMPARISON,
  comparisonKey,
  getComparison,
  type Competitor,
  type InitialComparison,
} from '~/lib/compare';
import {
  FALLBACK_RATES,
  RECEIVE_CURRENCIES,
  SEND_CURRENCIES,
  SOURCE_CURRENCY,
  getQuote,
  type PricingTier,
  type InitialQuote,
  type Quote,
  type ReceiveCurrency,
  type SendCurrency,
} from '~/lib/quote';

/*
 * Reusable rate comparison table (column-per-provider, Figma Compare page).
 *
 * Competitor columns show REAL, live data. The nsave backend has no
 * multi-provider comparison endpoint, so — mirroring what arrow-dev's
 * `WiseFxProvider` does server-side — the competitor rates/fees/markups come
 * from Wise's public comparison API via the `getComparison` server function
 * (see `~/lib/compare`). Providers, their rates, fees, hidden markups and payout
 * amounts are whatever Wise reports for the corridor + amount (e.g. Wise,
 * Remitly, Xoom, WorldRemit …), rendered with their real brand logos.
 *
 * Both modes send one of the three account currencies (`SEND_CURRENCIES` —
 * USD/EUR/GBP) into a local payout currency (`RECEIVE_CURRENCIES`), the same
 * corridors the calculator offers, so "Recipient gets" is always a real payout
 * rather than a hard-currency conversion.
 *
 * Two modes:
 *  - Uncontrolled (compare page): renders its own Amount / From / To selectors,
 *    defaulting to USD → EGP.
 *  - Controlled + visible selectors (send-money-home): pass `amount`/`source`/
 *    `target`/`plan` AND the `on*Change` handlers. The Amount/From/To selectors
 *    are then shown but bound to the SHARED state that also drives the calculator
 *    and the rates strip (two-way sync — editing either surface updates the
 *    other). The nsave column reflects the SAME live quote/plan the calculator
 *    shows (reuse `getQuote`).
 *
 * Competitor columns only ever show live data. `/compare` passes
 * `initialComparison` and `initialQuote`, fetched while the page server-renders,
 * so its HTML carries real rates for both nsave and the competitors. Until live data arrives otherwise, the table shows the nsave column alone
 * with a loading note; if the live comparison fails or comes back empty it
 * stays that way with a neutral "unavailable" note. We never stand in made-up
 * competitor figures.
 */

/**
 * Selector options carry the code and its display name, and the name is
 * language-dependent, so the list is built per render from the active bundle
 * rather than frozen at module scope in English.
 */
function currencyOptions(
  codes: readonly DisplayCurrency[],
  names: Record<DisplayCurrency, string>,
): { code: string; name: string }[] {
  return codes.map((code) => ({ code, name: names[code] }));
}

function renderPayoutFlag(code: string): ReactNode {
  return (
    <img
      src={currencyFlagSrc(code)}
      alt=""
      aria-hidden="true"
      className="size-8 rounded-full object-cover"
    />
  );
}

/**
 * Real brand logos for competitors returned by the Wise comparison API, copied
 * from Wise's CDN into `public/images/providers/`. Keyed by Wise provider alias.
 * Providers without a local asset fall back to a monogram badge.
 */
const PROVIDER_LOGOS: Record<string, string> = {
  wise: '/images/providers/wise.svg',
  remitly: '/images/providers/remitly.svg',
  xoom: '/images/providers/xoom.svg',
  'world-remit': '/images/providers/world-remit.svg',
  instarem: '/images/providers/instarem.svg',
  moneygram: '/images/providers/moneygram.svg',
  ofx: '/images/providers/ofx.svg',
  chase: '/images/providers/chase.svg',
  'bank-of-america': '/images/providers/bank-of-america.svg',
};

/** Max competitor columns shown alongside nsave (preserves the 4-column design). */
const MAX_COMPETITORS = 3;

type DisplayProvider = {
  key: string;
  name: string;
  isNsave?: boolean;
  logo?: string | null;
  /** `null` while the corridor's rate is still unknown. */
  rate: number | null;
  transferFee: number | null;
  markupFee: number;
  totalCost: number;
  recipientGets: number | null;
};

const money = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const rateFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Placeholder for a cell whose value is not known yet, as in the calculator. */
const PENDING = '···';

function formatAmount(value: number | null, currency: string): string {
  return value === null ? PENDING : `${money.format(value)} ${currency}`;
}

// Selector field styling, matched to the rewards page (RewardsCalculator): an
// outlined box (no fill) with a 20px radius, 72px tall, 1.5px border resting at
// black-6 and turning grey-900 on focus (border-box → no layout shift). The label
// is 17sb with a 20px gap to its field and pl-4 so it aligns with the field value.
const fieldLabelClass = 'mb-5 block pl-4 text-body font-semibold tracking-[-0.01em] text-grey-900';
const fieldBoxBase =
  'h-[72px] rounded-[20px] border-[1.5px] border-black-6 bg-transparent transition-colors';

function ProviderBadge({ provider }: { provider: DisplayProvider }) {
  // nsave now uses a real brand icon (same size/treatment as competitor logos).
  const logo = provider.isNsave ? '/images/providers/nsave.png' : provider.logo;
  if (logo) {
    return (
      <img
        src={logo}
        alt={provider.name}
        width={44}
        height={44}
        className="size-11 rounded-full"
        loading="lazy"
      />
    );
  }
  return (
    <span
      className="grid size-11 place-items-center rounded-full bg-grey-100 text-body font-semibold text-grey-600"
      aria-hidden="true">
      {provider.name.charAt(0)}
    </span>
  );
}

function CurrencySelect({
  label,
  value,
  options,
  renderFlag,
  onChange,
}: {
  label: string;
  value: string;
  options: { code: string; name: string }[];
  renderFlag: (code: string) => ReactNode;
  onChange: (code: string) => void;
}) {
  const current = options.find((c) => c.code === value) ?? options[0];
  return (
    <label className="block">
      <span className={fieldLabelClass}>{label}</span>
      <span
        className={cn(
          fieldBoxBase,
          'relative flex items-center gap-3 px-4 focus-within:border-grey-900',
        )}>
        {renderFlag(current.code)}
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="truncate text-body font-semibold text-ink [text-box-trim:none]">
            {current.code}
          </span>
          <span className="truncate text-body font-medium text-grey-900 [text-box-trim:none]">
            {current.name}
          </span>
        </span>
        <svg
          className="ml-auto size-5 text-grey-500"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true">
          <path
            d="M6 8l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          className="absolute inset-0 w-full cursor-pointer opacity-0">
          {options.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

/** Fallback nsave quote used before the first live quote / if upstream fails. */
function fallbackNsaveQuote(target: ReceiveCurrency, amount: number): Quote {
  const rate = FALLBACK_RATES[target];
  return { rate, fee: 1, amount: Math.max(amount - 1, 0) * rate };
}

export function RateComparison({
  amount: amountProp,
  source: sourceProp,
  target: targetProp,
  plan = 'PRO',
  showSelectors = true,
  showSavings = false,
  heading,
  initialComparison,
  initialQuote,
  onAmountChange,
  onSourceChange,
  onTargetChange,
}: {
  amount?: number;
  source?: string;
  target?: string;
  plan?: PricingTier;
  showSelectors?: boolean;
  showSavings?: boolean;
  heading?: string;
  /** Live comparison loaded during the server render; used while its key matches. */
  initialComparison?: InitialComparison | null;
  /** Live nsave quote loaded during the server render; used while its key matches. */
  initialQuote?: InitialQuote | null;
  /**
   * When any `on*Change` handler is provided the selectors become CONTROLLED and
   * bound to the parent's shared state (send-money-home two-way sync). When all
   * are absent the component keeps its own internal selector state (compare page).
   */
  onAmountChange?: (value: string) => void;
  onSourceChange?: (code: string) => void;
  onTargetChange?: (code: string) => void;
} = {}) {
  const t = useContent().ui.calculator;
  const [amountState, setAmount] = useState(DEFAULT_COMPARISON.amount);
  const [fromState, setFrom] = useState<string>(SOURCE_CURRENCY);
  const [toState, setTo] = useState<ReceiveCurrency>(
    DEFAULT_COMPARISON.targetCurrency as ReceiveCurrency,
  );

  const amount = amountProp ?? amountState;
  const from = sourceProp ?? fromState;
  const to = targetProp ?? toState;

  const handleAmountChange = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '');
    if (onAmountChange) onAmountChange(digits);
    else setAmount(Number(digits) || 0);
  };
  const handleFromChange = (code: string) => {
    if (onSourceChange) onSourceChange(code);
    else setFrom(code);
  };
  const handleToChange = (code: string) => {
    if (onTargetChange) onTargetChange(code);
    else setTo(code as ReceiveCurrency);
  };

  const fromOptions = currencyOptions(SEND_CURRENCIES, t.currencyNames);
  const toOptions = currencyOptions(RECEIVE_CURRENCIES, t.currencyNames);

  // The nsave column mirrors the calculator's live quote for the selected
  // corridor, which the upstream prices for any send currency into any payout.
  const liveSource = (SEND_CURRENCIES as readonly string[]).includes(from)
    ? (from as SendCurrency)
    : null;
  const liveTarget = (RECEIVE_CURRENCIES as readonly string[]).includes(to)
    ? (to as ReceiveCurrency)
    : null;
  const nsaveKey =
    liveSource && liveTarget ? `${liveSource}:${liveTarget}:${amount}:${plan}` : null;
  const [nsaveResult, setNsaveResult] = useState<{ key: string; quote: Quote } | null>(
    initialQuote ?? null,
  );
  const nsaveLive = nsaveKey && nsaveResult?.key === nsaveKey ? nsaveResult.quote : null;

  // Live competitor comparison (real Wise data). Keyed on pair + amount.
  const compareKey = amount > 0 ? comparisonKey(from, to, amount) : null;
  const [compareResult, setCompareResult] = useState<{
    key: string;
    competitors: Competitor[];
  } | null>(initialComparison ?? null);
  const liveCompetitors =
    compareKey && compareResult?.key === compareKey ? compareResult.competitors : null;

  // Debounced, race-safe live nsave quote (same render-derived pattern as the
  // calculator). setState only runs inside the async callbacks.
  useEffect(() => {
    if (!nsaveKey || !liveSource || !liveTarget) return;
    const key = nsaveKey;
    const timer = setTimeout(() => {
      void getQuote({
        data: {
          sourceCurrency: liveSource,
          targetCurrency: liveTarget,
          amount,
          pricingTier: plan,
        },
      })
        .then((live) => setNsaveResult({ key, quote: live }))
        // Swallowed, not fallen back from: an unanswered corridor is already
        // resolved by `nsaveQuote` below, to the per-USD estimate or to pending
        // where there is no honest stand-in. Repeating that policy here would put
        // the guard that keeps EUR/GBP off a per-USD rate in two places, only one
        // of which a server-rendered test can reach. A handler is still needed so
        // the rejection is not unhandled.
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [nsaveKey, liveSource, liveTarget, amount, plan]);

  // Debounced, race-safe live competitor comparison. On failure we store an empty
  // list so the render shows the "unavailable" state for this key.
  useEffect(() => {
    if (!compareKey) return;
    const key = compareKey;
    const timer = setTimeout(() => {
      void getComparison({ data: { sourceCurrency: from, targetCurrency: to, amount } })
        .then((competitors) => setCompareResult({ key, competitors }))
        .catch(() => setCompareResult({ key, competitors: [] }));
    }, 300);
    return () => clearTimeout(timer);
  }, [compareKey, from, to, amount]);

  // `loading` until this key's comparison settles (always, on the server render);
  // `unavailable` once it has failed or returned no providers.
  const competitorStatus: 'none' | 'loading' | 'live' | 'unavailable' = !compareKey
    ? 'none'
    : liveCompetitors === null
      ? 'loading'
      : liveCompetitors.length
        ? 'live'
        : 'unavailable';

  // The nsave column may only ever come from an nsave quote: the live one, or the
  // same per-USD static estimate the calculator falls back to. Deriving it from a
  // shared mid-market rate once published a competitor's rate as ours with a zero fee and an
  // ungrossed payout — whenever the comparison landed before our own quote (both
  // are fetched in parallel) or the quote failed on a non-USD send, where nothing
  // ever fills it in. A corridor with no honest nsave number stays pending
  // instead, matching `CurrencyCalculator`.
  const nsaveQuote =
    nsaveLive ??
    (liveSource === SOURCE_CURRENCY && liveTarget ? fallbackNsaveQuote(liveTarget, amount) : null);

  const nsaveRow: DisplayProvider = nsaveQuote
    ? {
        key: 'nsave',
        name: 'nsave',
        isNsave: true,
        rate: nsaveQuote.rate,
        transferFee: nsaveQuote.fee,
        markupFee: 0,
        totalCost: nsaveQuote.fee,
        recipientGets: nsaveQuote.amount,
      }
    : {
        key: 'nsave',
        name: 'nsave',
        isNsave: true,
        rate: null,
        transferFee: null,
        markupFee: 0,
        totalCost: 0,
        recipientGets: null,
      };

  const competitorRows: DisplayProvider[] = (liveCompetitors ?? [])
    .slice(0, MAX_COMPETITORS)
    .map((c) => ({
      key: c.alias,
      name: c.name,
      logo: PROVIDER_LOGOS[c.alias] ?? null,
      rate: c.rate,
      transferFee: c.fee,
      markupFee: c.markup,
      totalCost: c.fee + c.markup,
      recipientGets: c.receivedAmount,
    }));

  const results: DisplayProvider[] = [nsaveRow, ...competitorRows];

  const known = results.flatMap((r) => (r.recipientGets === null ? [] : [r.recipientGets]));
  const savings =
    nsaveRow.recipientGets === null || !known.length
      ? 0
      : Math.max(nsaveRow.recipientGets - Math.min(...known), 0);

  const rows = [
    {
      id: 'youSend',
      label: t.youSend,
      // Same send amount across every provider column (input amount + source).
      render: (_r: DisplayProvider) => `${money.format(amount)} ${from}`,
    },
    {
      id: 'transferFee',
      label: t.transferFee,
      render: (r: DisplayProvider) => formatAmount(r.transferFee, from),
    },
    {
      id: 'rate',
      label: t.exchangeRate,
      render: (r: DisplayProvider) => (r.rate === null ? PENDING : rateFormat.format(r.rate)),
    },
    {
      id: 'recipient',
      label: t.recipientGets,
      emphasise: true,
      render: (r: DisplayProvider) => formatAmount(r.recipientGets, to),
    },
  ];

  return (
    <Section className="!pt-[104px] md:!pt-[120px]">
      {heading && <SectionHeading title={heading} />}
      {/* 56px gap (mt-14) below the SectionHeading divider so the selectors don't
          crowd the line above them. */}
      {showSelectors && (
        <div className="mt-14 grid grid-cols-2 gap-6 md:grid-cols-3">
          <label className="col-span-2 block md:col-span-1">
            <span className={fieldLabelClass}>{t.amount}</span>
            <span
              className={cn(fieldBoxBase, 'flex items-center px-4 focus-within:border-grey-900')}>
              <input
                inputMode="numeric"
                value={amount ? amount.toLocaleString('en-US') : ''}
                onChange={(e) => handleAmountChange(e.target.value)}
                aria-label={t.amountLabel}
                className="w-full bg-transparent text-body font-medium tabular-nums text-ink outline-none"
              />
            </span>
          </label>
          <CurrencySelect
            label={t.from}
            value={from}
            options={fromOptions}
            renderFlag={renderPayoutFlag}
            onChange={handleFromChange}
          />
          <CurrencySelect
            label={t.to}
            value={to}
            options={toOptions}
            renderFlag={renderPayoutFlag}
            onChange={handleToChange}
          />
        </div>
      )}

      <p className="mt-6 text-body-s text-ink-secondary empty:hidden" aria-live="polite">
        {competitorStatus === 'loading'
          ? t.competitorRatesLoading
          : competitorStatus === 'unavailable'
            ? t.competitorRatesUnavailable
            : null}
      </p>

      {/* table-fixed → every column (the label column + nsave + up to 3
          competitors) is an equal fraction of the width, so the grid adapts to the
          competitor count (5 cols with 3 competitors, 4 with 2, 3 with 1). */}
      <div className="mt-10 overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
          <thead>
            <tr>
              <th />
              {results.map((r) => (
                <th
                  key={r.key}
                  className={cn('px-4 py-6 text-center align-middle', r.isNsave && 'bg-grey-25')}>
                  <span className="flex flex-col items-center gap-4">
                    <ProviderBadge provider={r} />
                    <span className="text-body font-semibold text-grey-1000">{r.name}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-black-6">
                <th
                  scope="row"
                  className="h-[116px] px-2 text-left align-middle text-body font-semibold text-ink">
                  {row.label}
                </th>
                {results.map((r) => (
                  <td
                    key={r.key}
                    className={cn(
                      'h-[116px] px-4 text-center align-middle text-body-m',
                      r.isNsave && 'bg-grey-25',
                      row.emphasise
                        ? r.isNsave
                          ? 'font-semibold text-ink'
                          : 'font-medium text-red-500'
                        : 'text-grey-900',
                    )}>
                    {row.render(r)}
                    {row.id === 'recipient' && r.isNsave && showSavings && savings > 0 && (
                      <span className="mt-2 block text-body-s font-medium text-green">
                        {t.saveUpTo} {money.format(savings)} {to}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
